import { createReducer } from 'redux-starter-kit';
import i18next from 'i18next';
import _ from 'lodash';

import { SupplierDataChangeApi } from './SupplierDataChangeApi';
import { handleError } from '../../modules/utils/handleError';
import * as Dict from '../../modules/utils/Dict';
import Notice from '../../modules/utils/Notice';
import { NCALayerSign } from '../../components/NCALayer/NCALayerSign';
import { history } from '../../_helpers/history';
import paths from '../../_helpers/paths';

/**
 * Constants
 */

export const supplierDataChangeModule = 'supplierDataChange';
const LOADING = `${supplierDataChangeModule}/LOADING`;
const TABLE_DATA = `${supplierDataChangeModule}/TABLE_DATA`;
const NOTIFICATIONS_LOADING = `${supplierDataChangeModule}/NOTIFICATIONS_LOADING`;
const NOTIFICATIONS_DATA = `${supplierDataChangeModule}/NOTIFICATIONS_DATA`;
const ANNUL_LOADING = `${supplierDataChangeModule}/ANNUL_LOADING`;
const CLEAR_STATE = `${supplierDataChangeModule}/CLEAR_STATE`;

/**
 * Reducer
 */

const initialState = {
  loading: false,
  tableData: {
    content: [],
    pageNumber: 1,
    totalElements: 0
  },
  notificationsLoading: false,
  notifications: {
    content: [],
    pageNumber: 1,
    totalElements: 0
  },
  annulLoading: false
};

export default createReducer(initialState, {
  [LOADING]: (state, action) => {
    state.loading = action.payload;
  },
  [TABLE_DATA]: (state, action) => {
    const filter = action.filter || {};
    if (filter.pageNumber && filter.pageNumber !== 1) {
      state.tableData.content = [
        ...state.tableData.content,
        ...action.payload.content
      ];
    } else {
      state.tableData = action.payload;
    }
  },
  [NOTIFICATIONS_LOADING]: (state, action) => {
    state.notificationsLoading = action.payload;
  },
  [ANNUL_LOADING]: (state, action) => {
    state.annulLoading = action.payload;
  },
  [NOTIFICATIONS_DATA]: (state, action) => {
    state.notifications = {
      ...action.payload,
      totalElements:
        action.payload.pageNumber === 1
          ? action.payload.totalElements
          : state.notifications.totalElements
    };
  },
  [CLEAR_STATE]: () => initialState
});

/**
 * Actions
 */

export const clearState = () => ({ type: CLEAR_STATE });

export const loadServiceProviders = filter => async dispatch => {
  try {
    dispatch({ type: LOADING, payload: true });
    const filterString = JSON.stringify({ ...filter, isSupervisorIinExists: true });
    const { data } = await SupplierDataChangeApi.getServiceProviders(filterString);
    const result = data.result || data;
    if (result && result.content) {
      for (let row of result.content) {
        row.providerLocation = await Dict.item('60', row.location);
      }
      dispatch({
        type: TABLE_DATA,
        payload: result,
        filter
      });
    } else {
      handleError(data, i18next.t('supplierDataChange_errorLoadTable'));
    }
  } catch (error) {
    handleError(error, i18next.t('supplierDataChange_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};

export const signNotify = ({
  serviceProviderId,
  designCapacity,
  legalAddress
}) => async dispatch => {
  try {
    dispatch({ type: LOADING, payload: true });
    const body = {
      serviceProviderId: Number(serviceProviderId),
      designCapacity: Number(designCapacity),
      legalAddress
    };
    const { data } = await SupplierDataChangeApi.getSignString(body);
    const signature = await NCALayerSign(data.result);
    if (!signature) return;
    await SupplierDataChangeApi.createNotify({ ...body, signature });
    Notice.success(i18next.t('supplierDataChange_notifySuccess'));
    history.push(paths.homePage);
  } catch (error) {
    const errorCode = _.get(error, 'response.data.errors[0].code');
    if (errorCode === 'error.creating_already_existing_service_provider_notification') {
      Notice.error(i18next.t('supplierDataChange_alreadyExistingNotification'));
    } else {
      handleError(error, i18next.t('supplierDataChange_notifyError'));
    }
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};

export const loadNotifications = filter => async (dispatch, getState) => {
  try {
    dispatch({ type: NOTIFICATIONS_LOADING, payload: true });
    const requesterIin = getState().login.user.iin;
    const filterString = JSON.stringify({ ...filter, requesterIin });
    const { data } = await SupplierDataChangeApi.getNotifications(filterString);
    const result = data.result || data;
    if (result && result.content) {
      dispatch({ type: NOTIFICATIONS_DATA, payload: result });
    } else {
      handleError(data, i18next.t('supplierDataChange_errorLoadNotifications'));
    }
  } catch (error) {
    handleError(error, i18next.t('supplierDataChange_errorLoadNotifications'));
  } finally {
    dispatch({ type: NOTIFICATIONS_LOADING, payload: false });
  }
};

export const annulNotification = (serviceProviderId, filter) => async dispatch => {
  try {
    dispatch({ type: ANNUL_LOADING, payload: true });
    await SupplierDataChangeApi.annulNotify(serviceProviderId);
    Notice.success(i18next.t('supplierDataChange_annulSuccess'));
    dispatch(loadNotifications(filter));
    return true;
  } catch (error) {
    handleError(error, i18next.t('supplierDataChange_annulError'));
    return false;
  } finally {
    dispatch({ type: ANNUL_LOADING, payload: false });
  }
};
